/**
 * API constants for EqualIQ frontend
 * Although this is a constant file, it uses dynamic values for API endpoints to support switching test environments.
 */

import environments from './environments.json';

interface EnvironmentConfig {
  displayName: string;
  apiEndpoint: string;
}

const environmentsTyped: Record<string, EnvironmentConfig> = environments;

// Vite wipes `process.env` from the runtime bundle (vite.config.ts: `'process.env': {}`)
// and only exposes `VITE_*`-prefixed vars on `import.meta.env`. Reading
// `process.env.VITE_APP_ENVIRONMENT` would always be undefined →
// `isNonProdEnvironment()` would always return true → production lockdown
// would be broken. Read from import.meta.env so the prod build's
// `VITE_APP_ENVIRONMENT=production` actually gates the switcher.
export function isNonProdEnvironment(): boolean {
  return import.meta.env.VITE_APP_ENVIRONMENT !== 'production';
}

const BACKEND_ENVIRONMENT_STORAGE_KEY = 'backend-environment';

// Resolution order in nonprod: localStorage override (QA escape hatch) →
// VITE_BACKEND_ENVIRONMENT (per-Vercel-branch build var) → 'equaliq' fallback.
export function getApiEnvironment(): string {
  if (isNonProdEnvironment()) {
    return (
      localStorage.getItem(BACKEND_ENVIRONMENT_STORAGE_KEY) ||
      import.meta.env.VITE_BACKEND_ENVIRONMENT ||
      'equaliq'
    );
  }
  return 'equaliq';
}

export function setApiEnvironment(tag: string): void {
  localStorage.setItem(BACKEND_ENVIRONMENT_STORAGE_KEY, tag);
}

function getApiEndpoint(tag: string): string {
  const env = environmentsTyped[tag];
  if (!env) {
    throw new Error(`No environment found for tag: ${tag}`);
  }
  return env.apiEndpoint;
}

// VITE_API_BASE_URL behavior:
//   undefined  → look up environments[tag].apiEndpoint
//   "https://..."  → absolute URL passthrough (local-dev override)
function getApiBaseUrl(): string {
  const override = import.meta.env.VITE_API_BASE_URL;
  if (override !== undefined && override !== '') return String(override).replace(/\/+$/, '');
  return getApiEndpoint(getApiEnvironment());
}

export const API_BASE_URL = getApiBaseUrl();

export const MOCK_API_DELAY = 30; // milliseconds — centralized for all mock API calls